import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { InitialAssessmentForm } from '../../data/initial-assessment-form';
import { ApplicantDetails } from '../../data/applicant-details';
import { Person } from '../../data/person';
import { PersonDialogComponent } from '../person-dialog/person-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { IFormTypes } from '../../models/form-types';
import { LookupItem } from '../../models/lookup-item';
import { NavigationService } from '../../services/navigation.service';
import { AssessmentService } from '../../services/assessment.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { requiredListWhenYesValidator } from '../../validators/required-list-when-yes-validator';
import { IVisaType } from '../../data/visa-type';


export interface NonIncludedChild {
  firstName: string;
  surname: string;
  dob: string;
  countryResidence: { value: string };
  title?: { value: string };
}

@Component({
  selector: 'app-non-included-children',
  templateUrl: './non-included-children.component.html',
  styleUrl: './non-included-children.component.scss'
})
export class NonIncludedChildrenComponent {


    @Input()model!: InitialAssessmentForm;
    @Input()formTypes!: IFormTypes;
    @Output() formReady = new EventEmitter<FormGroup>();
    @Input()applicantText: any;
    @Input()selectedForm: any;
    @Input()isVevo: any;
    @Input()isVevoEmployee: any;
    @Input()titles!: LookupItem[];
    @Input()relationTypes!: LookupItem[];
    @Input()maritalStatuses!: LookupItem[];
    @Input()educationLevels!: LookupItem[];
    @Input()countries!: LookupItem[];
    @Input()visaTypes!: IVisaType[];
    @Input()submitAttempted: boolean = false;

  nonIncludedChildren: NonIncludedChild[] = [];
  displayedColumns: string[] = ['name', 'dob', 'countryResidence', 'edit', 'delete'];
  isParent: boolean = false;
  

    private destroy$ = new Subject<void>();
    private breakpointObserver = inject(BreakpointObserver);
    isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
      map(result => result.matches),
      takeUntil(this.destroy$)
    );
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

formBuilder = inject(NonNullableFormBuilder);

nonIncludedChildrenForm = this.formBuilder.group({
    childrenNotIncluded: this.formBuilder.control('')
},{
    validators: requiredListWhenYesValidator([
              {
                controlName: 'childrenNotIncluded',
                errorKey: 'childrenNotIncludedRequired',
                getItems: () => this.model?.applicantDetails?.childrenNotIncludedList ?? []
              }])
});

constructor(private dialog: MatDialog, private assessmentService: AssessmentService) { }

  ngOnInit(): void {
    this.initializeForm();
    this.isParent = this.assessmentService.isParent();
    // this has been commented out as even though it makes no sense to have a yes/no question with no requirement to answer, it is like that in the old app and in other places on the app and we need to keep it the same for now.
    // leaving it here commented out for visibility for future developers that may wonder why we have a yes/no question with no required answer
    // if(this.isParent) {
    //   this.nonIncludedChildrenForm.controls.childrenNotIncluded.setValidators(Validators.required);
    //   this.nonIncludedChildrenForm.controls.childrenNotIncluded.updateValueAndValidity();
    // }
    this.formReady.emit(this.nonIncludedChildrenForm);

  }

  initializeForm(){
        
    this.model = this.model || new InitialAssessmentForm();
    this.model.applicantDetails = this.model.applicantDetails || new ApplicantDetails();
        
    this.nonIncludedChildrenForm?.patchValue({
      childrenNotIncluded: this.model.applicantDetails.childrenNotIncluded
    });  

    this.nonIncludedChildrenForm.valueChanges.subscribe(value => {
      if (this.model.applicantDetails && value) {
          Object.assign(this.model.applicantDetails, value);
      }
    });

  }

  getTitle(child: NonIncludedChild): string {
    return child.title?.value || '';
  }


  add() {
     const dialogRef = this.dialog.open(PersonDialogComponent, {
          panelClass: 'dialog-panel-large',
          maxHeight: 'none',
          data: { person: null, 
          personType: 'nonIncludedChild',
          applicantName: this.model.applicantDetails?.firstName || 'the applicant',
          isVevo: this.isVevo,
          isVevoEmployee: this.isVevoEmployee, 
          titles: this.titles, 
          relationTypes: this.relationTypes, 
          maritalStatuses: this.maritalStatuses, 
          educationLevels: this.educationLevels, 
          countries: this.countries, 
          visaTypes: this.visaTypes 
        }
        });
        
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.model.applicantDetails!.childrenNotIncludedList.push(result);
            this.model.applicantDetails!.childrenNotIncludedList = [...this.model.applicantDetails!.childrenNotIncludedList];
            this.nonIncludedChildrenForm.controls.childrenNotIncluded.updateValueAndValidity();
          }
        });
  }

edit(index: number): void {
    const person = this.model.applicantDetails!.childrenNotIncludedList[index];
    const dialogRef = this.dialog.open(PersonDialogComponent, {
      panelClass: 'dialog-panel-large',
      maxHeight: 'none',
          data: { person: person, 
          personType: 'nonIncludedChild',
          applicantName: this.model.applicantDetails?.firstName || 'the applicant',
          isVevo: this.isVevo,
          isVevoEmployee: this.isVevoEmployee, 
          titles: this.titles, 
          relationTypes: this.relationTypes, 
          maritalStatuses: this.maritalStatuses, 
          educationLevels: this.educationLevels, 
          countries: this.countries, 
          visaTypes: this.visaTypes 
        }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.model.applicantDetails!.childrenNotIncludedList[index] = result;
        this.model.applicantDetails!.childrenNotIncludedList = [...this.model.applicantDetails!.childrenNotIncludedList];
        this.nonIncludedChildrenForm.controls.childrenNotIncluded.updateValueAndValidity();
      }
    });
  }

  remove(index: number): void {
    this.model.applicantDetails!.childrenNotIncludedList.splice(index, 1);
    this.model.applicantDetails!.childrenNotIncludedList = [...this.model.applicantDetails!.childrenNotIncludedList];
    this.nonIncludedChildrenForm.controls.childrenNotIncluded.updateValueAndValidity();
  }


  get otherChildrenQuestionText(): string {
     return `Does the main applicant or the applicant's Spouse/Partner have any other children who will <b>NOT</b> be included in this application?`;
  }

    get safeChildrenNotIncludedList(): Person[] {
        return this.model.applicantDetails?.childrenNotIncludedList ?? [];
    }

}
